import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { useMemo, useState } from 'react';

import type { PickerSize } from '../pickerPrimitiveStyles';
import ComboboxContext from './ComboboxContext';

type ComboboxValueType<Value, Multiple extends boolean | undefined> = Multiple extends true
  ? Value[]
  : Value;

export type ComboboxProps<
  Value,
  Multiple extends boolean | undefined = false,
> = ComboboxPrimitive.Root.Props<Value, Multiple> & {
  'aria-invalid'?: boolean;
  isLoading?: boolean;
  error?: Error | null;
  size?: PickerSize;
  onChange?: (value: ComboboxValueType<Value, Multiple> | null) => void;
};

const Combobox = <Value, Multiple extends boolean | undefined = false>({
  autoHighlight = true,
  isLoading = false,
  disabled = false,
  size = 'large',
  error,
  children,
  items,
  filteredItems,
  value,
  isItemEqualToValue,
  multiple,
  ...props
}: ComboboxProps<Value, Multiple>) => {
  const handleValueChange: ComboboxPrimitive.Root.Props<Value, Multiple>['onValueChange'] = (
    newValue,
    eventDetails,
  ) => {
    props.onValueChange?.(newValue, eventDetails);
    props.onChange?.(newValue);
  };

  const [chipsAnchor, setChipsAnchor] = useState<HTMLDivElement | null>(null);
  const [inputAnchor, setInputAnchor] = useState<HTMLDivElement | null>(null);

  // Use filteredItems when present, otherwise items.
  // If the selected value is not in that source list, add it there so the label still renders.
  const itemsWithValue = useMemo(() => {
    const sourceItems = filteredItems ?? items;

    if (!value || (Array.isArray(sourceItems) && (sourceItems as Value[]).length > 0)) {
      return sourceItems;
    }

    const selected = multiple ? (value as Value[]) : [value as Value];
    return selected as unknown as typeof sourceItems;
  }, [filteredItems, items, value, multiple]);

  return (
    <ComboboxContext.Provider
      value={{
        size,
        isLoading,
        error,
        disabled,
        chipsAnchor,
        setChipsAnchor,
        inputAnchor,
        setInputAnchor,
      }}
    >
      <ComboboxPrimitive.Root
        autoHighlight={autoHighlight}
        items={itemsWithValue}
        filteredItems={filteredItems}
        value={value}
        isItemEqualToValue={isItemEqualToValue}
        multiple={multiple}
        {...props}
        onValueChange={handleValueChange}
      >
        {children}
      </ComboboxPrimitive.Root>
    </ComboboxContext.Provider>
  );
};

export default Combobox;
